import { Component } from 'react';

import FeaturesTilesComponent from '../../components/ui/features-tiles/features-tiles.component';
import { FeatureDetailsComponent } from '../../components/ui/feature-details/feature-details.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import RatingDisplayComponent from '../../components/ui/rating-display/rating-display.component';
import { RelatedLinksComponent } from '../../components/ui/related-links/related-links.component';
import { lazyInject } from '../../context/module';
import { EnvironmentService } from '../../services/environment.service';
import { MotivesService } from '../../services/motives.service';
import { FeatureFlagsEmission, SettingsService } from '../../services/settings.service';
import { RouteProps, SeoFeature } from '../../types/route-types';
import { MotiveLocationTypes } from '../../utils/request.utils';
import { RouteUtils } from '../../utils/route.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import { get } from '../../utils/lodash-min';
import { isWorkspaceActive, showPageChromeAroundEditor, showPageFurniture, workspaceWrapperStyle,
    showToolNameAboveEditor, editorOpenWrapperStyle, editorOpenCardStyle,
    VIEWPORT_OWNED_MEDIA_QUERY, editorOwnsViewport } from '../../utils/workspace-mode';
import MotiveFrame from '../motive-frames/motive-frame.component';
import sharedStyles from './single-page.module.scss';
import styles from './single-page-base.component.module.scss';
import dynamic from 'next/dynamic';
import { hasSkipLink, SkipTarget } from '../pdf-shared/skip-link'; // W8-SHELL (UX-10)

// Loading placeholder shown while editor chunk downloads
const EditorLoading = () => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: 200, gap: '1rem', color: '#94a3b8',
    }}>
        <div style={{
            width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#6366f1',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
        }} />
        <span style={{ fontSize: '0.875rem' }}>Loading tool...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
);


// S4 family #1 — subtitle converter matrix (FEATURE_EXPANSION_RESEARCH.md §5.1).
// All 156 SUBTITLE↔SUBTITLE pair slugs point to the same dynamic import so
// the chunk is loaded once and reused. URL slug picks the from / to defaults.
const SUBTITLE_FORMATS = ['srt', 'vtt', 'ass', 'ssa', 'sbv', 'sub', 'lrc', 'smi', 'ttml', 'stl', 'dfxp', 'txt', 'csv'];
const subtitleConverterEditor = dynamic(() => import('./subtitle-converter/subtitle-converter.component'), { loading: EditorLoading });
const subtitleEntries: Record<string, any> = {};
for (const from of SUBTITLE_FORMATS) {
    for (const to of SUBTITLE_FORMATS) {
        if (from !== to) subtitleEntries[`convert-${from}-to-${to}`] = subtitleConverterEditor;
    }
}

// S5 family #8 — font converter matrix (FEATURE_EXPANSION_RESEARCH.md §5.8).
const FONT_FORMATS = ['ttf', 'otf', 'woff', 'woff2', 'eot'];
const fontConverterEditor = dynamic(() => import('./font-converter/font-converter.component'), { loading: EditorLoading });
const fontEntries: Record<string, any> = {};
for (const from of FONT_FORMATS) {
    for (const to of FONT_FORMATS) {
        if (from !== to) fontEntries[`convert-${from}-to-${to}`] = fontConverterEditor;
    }
}

// S5 family #9 — 3D model converter matrix (FEATURE_EXPANSION_RESEARCH.md §5.9).
// The wrapper renders the static shell (selects, file picker placeholder)
// server-side, then swaps in the real three.js-powered editor after
// hydration. Crawlers and slow-JS browsers see the actual layout instead
// of a generic spinner.
const MODEL3D_FORMATS = ['stl', 'obj', 'glb', 'gltf', 'ply', '3mf'];
const model3dConverterEditor = dynamic(
    () => import('./model3d-converter/model3d-converter.wrapper'),
    { loading: EditorLoading },
);
const model3dEntries: Record<string, any> = {};
for (const from of MODEL3D_FORMATS) {
    for (const to of MODEL3D_FORMATS) {
        if (from !== to) model3dEntries[`convert-${from}-to-${to}`] = model3dConverterEditor;
    }
}

// Format HUBS ({ext}-converter) reuse the same in-browser editors; the editor's
// From/To selects let the user pick the target. stl/txt/csv/sub are SUBTITLE hubs;
// the 3D hubs are obj/glb/gltf/3mf/ply (NOT stl — that would clobber the subtitle
// stl-converter since model3dEntries is spread last).
const MODEL3D_HUB_FORMATS = ['obj', 'glb', 'gltf', '3mf', 'ply'];
for (const f of SUBTITLE_FORMATS) subtitleEntries[`${f}-converter`] = subtitleConverterEditor;
for (const f of FONT_FORMATS) fontEntries[`${f}-converter`] = fontConverterEditor;
for (const f of MODEL3D_HUB_FORMATS) model3dEntries[`${f}-converter`] = model3dConverterEditor;
// CAD import (import-only): convert-{cad}-to-{mesh} pairs + {cad}-converter hubs all
// use the same model3d editor (step/iges/brep lazy-load occt-import-js, 3dm loads
// rhino3dm). CAD never a target.
const CAD_IMPORT_FORMATS = ['step', 'iges', 'brep', '3dm'];
for (const cad of CAD_IMPORT_FORMATS) {
    model3dEntries[`${cad}-converter`] = model3dConverterEditor;
    for (const to of MODEL3D_FORMATS) model3dEntries[`convert-${cad}-to-${to}`] = model3dConverterEditor;
}

// S6 family #10 — PDF Studio (8 verb slugs share one editor; pdfjs/pdf-lib touch browser
// APIs at module load so ssr: false prevents SSR crashes).
const pdfStudioEditor = dynamic(
    () => import('../pdf-studio/pdf-studio-root.component'),
    { loading: EditorLoading, ssr: false },
);

// Edit PDF (/pdf-editor). ssr:false because pdf.js and pdf-lib touch browser APIs at module
// load. The gate below lives OUT HERE in the statically-imported shell (2026-09-02 RULING):
// this dynamic() loader is only evaluated when getEditor() actually returns <PdfEditor/>, so
// an api-sourced `false` requests no pdf-editor chunk at all.
const pdfEditorEditor = dynamic(
    () => import('../pdf-editor/pdf-editor-root.component'),
    { loading: EditorLoading, ssr: false },
);

const EditPdfPlaceholder = () => (
    // Neutral and SIZED, rendered at the same height as whichever panel follows it, so
    // neither state causes a layout jump. Never the words "coming soon" — this state means
    // "we do not know yet", not "off". (2026-09-02)
    <div data-testid="pdf-editor-placeholder" style={{ minHeight: 520 }} aria-busy="true" />
);

// The flag-off copy is DUPLICATED here on purpose (2026-09-02): this shell is statically
// imported, and importing wave 2's views/notices.copy.ts would pull the editor's copy table
// into every single-page-converter bundle — the exact thing the provenance gate exists to
// avoid. The same sentence is CopyKey `flag.soon`; T10's notices.copy.test.ts asserts the two
// match, so a reword cannot land in only one of them.
const EditPdfComingSoon = () => (
    <div data-testid="pdf-editor-coming-soon" style={{ minHeight: 520 }}>
        <p><strong>Editing PDF text in your browser is on the way.</strong> In the meantime these
            PDF tools work today:</p>
        <ul>
            <li><a href="/rotate-pdf">Rotate PDF</a></li>
            <li><a href="/delete-pages-pdf">Delete pages</a></li>
            <li><a href="/reorder-pdf">Reorder pages</a></li>
            <li><a href="/merge-pdf">Merge PDF</a></li>
            <li><a href="/compress-pdf">Compress PDF</a></li>
        </ul>
    </div>
);

// Dynamic imports — each editor loads only on its page (code splitting, ssr: true by default = no flicker)
const editorMap: Record<string, any> = {
    ...subtitleEntries,
    ...fontEntries,
    ...model3dEntries,
    'font-face-css-generator': dynamic(() => import('./font-face-css-generator/font-face-css-generator.component'), { loading: EditorLoading }),
    'font-subsetter': dynamic(() => import('./font-subsetter/font-subsetter.component'), { loading: EditorLoading }),
    'url-encoder': dynamic(() => import('./url-encode/url-encode.component').then(m => ({ default: m.URLEncodeComponent })), { loading: EditorLoading }),
    'url-decoder': dynamic(() => import('./url-decode/url-decode.component').then(m => ({ default: m.URLDecodeComponent })), { loading: EditorLoading }),
    'word-counter': dynamic(() => import('./word-count/word-count.component').then(m => ({ default: m.WordCountComponent })), { loading: EditorLoading }),
    'base64-encoder': dynamic(() => import('./base64-encode/base64-encode.component').then(m => ({ default: m.Base64EncodeComponent })), { loading: EditorLoading }),
    'base64-decoder': dynamic(() => import('./base64-decode/base64-decode.component').then(m => ({ default: m.Base64DecodeComponent })), { loading: EditorLoading }),
    'color-converter': dynamic(() => import('./color-converter/color-converter.component'), { loading: EditorLoading }),
    'number-base-converter': dynamic(() => import('./number-base-converter/number-base-converter.component'), { loading: EditorLoading }),
    'text-case-converter': dynamic(() => import('./text-case-converter/text-case-converter.component'), { loading: EditorLoading }),
    'json-yaml-xml-converter': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'hash-generator': dynamic(() => import('./hash-generator/hash-generator.component'), { loading: EditorLoading }),
    'image-to-text': dynamic(() => import('./image-to-text/image-to-text.component'), { loading: EditorLoading }),
    'markdown-html-converter': dynamic(() => import('./markdown-html-converter/markdown-html-converter.component'), { loading: EditorLoading }),
    'lorem-ipsum-generator': dynamic(() => import('./lorem-ipsum-generator/lorem-ipsum-generator.component'), { loading: EditorLoading }),
    // Data format convert routes — reuse existing converter components
    'convert-json-to-yaml': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'convert-yaml-to-json': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'convert-json-to-xml': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'convert-xml-to-json': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'convert-yaml-to-xml': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'convert-xml-to-yaml': dynamic(() => import('./json-yaml-xml-converter/json-yaml-xml-converter.component'), { loading: EditorLoading }),
    'convert-markdown-to-html': dynamic(() => import('./markdown-html-converter/markdown-html-converter.component'), { loading: EditorLoading }),
    'convert-html-to-markdown': dynamic(() => import('./markdown-html-converter/markdown-html-converter.component'), { loading: EditorLoading }),
    // Phase 2: CodeEditorComponent serves all formatters, minifiers, validators
    'json-formatter': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'json-minifier': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'validate-json': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'css-formatter': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'css-minifier': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'html-formatter': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'html-minifier': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'js-formatter': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'js-minifier': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'sql-formatter': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'validate-yaml': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    'validate-xml': dynamic(() => import('./code-editor/code-editor.component'), { loading: EditorLoading }),
    // Phase 2: Standalone tools
    'qr-code-generator': dynamic(() => import('./qr-code-generator/qr-code-generator.component'), { loading: EditorLoading }),
    'password-generator': dynamic(() => import('./password-generator/password-generator.component'), { loading: EditorLoading }),
    'regex-tester': dynamic(() => import('./regex-tester/regex-tester.component'), { loading: EditorLoading }),
    'text-diff': dynamic(() => import('./text-diff/text-diff.component'), { loading: EditorLoading }),
    'uuid-generator': dynamic(() => import('./uuid-generator/uuid-generator.component'), { loading: EditorLoading }),
    'cron-generator': dynamic(() => import('./cron-generator/cron-generator.component'), { loading: EditorLoading }),
    'jwt-decoder': dynamic(() => import('./jwt-decoder/jwt-decoder.component'), { loading: EditorLoading }),
    'chmod-calculator': dynamic(() => import('./chmod-calculator/chmod-calculator.component'), { loading: EditorLoading }),
    'aspect-ratio-calculator': dynamic(() => import('./aspect-ratio-calculator/aspect-ratio-calculator.component'), { loading: EditorLoading }),
    // Phase 2: Bidirectional tools
    'convert-csv-to-json': dynamic(() => import('./csv-converter/csv-converter.component'), { loading: EditorLoading }),
    'convert-json-to-csv': dynamic(() => import('./csv-converter/csv-converter.component'), { loading: EditorLoading }),
    'convert-timestamp-to-date': dynamic(() => import('./timestamp-converter/timestamp-converter.component'), { loading: EditorLoading }),
    'convert-date-to-timestamp': dynamic(() => import('./timestamp-converter/timestamp-converter.component'), { loading: EditorLoading }),
    'timestamp-converter': dynamic(() => import('./timestamp-converter/timestamp-converter.component'), { loading: EditorLoading }),
    'html-entity-encoder': dynamic(() => import('./html-entity/html-entity.component'), { loading: EditorLoading }),
    'html-entity-decoder': dynamic(() => import('./html-entity/html-entity.component'), { loading: EditorLoading }),
    // S1 family #3 — SEO / web generators (FEATURE_EXPANSION_RESEARCH.md §5.3)
    'favicon-generator': dynamic(() => import('./favicon-generator/favicon-generator.component'), { loading: EditorLoading }),
    'og-image-generator': dynamic(() => import('./og-image-generator/og-image-generator.component'), { loading: EditorLoading }),
    'twitter-card-generator': dynamic(() => import('./twitter-card-generator/twitter-card-generator.component'), { loading: EditorLoading }),
    'meta-tag-generator': dynamic(() => import('./meta-tag-generator/meta-tag-generator.component'), { loading: EditorLoading }),
    'json-ld-schema-generator': dynamic(() => import('./json-ld-schema-generator/json-ld-schema-generator.component'), { loading: EditorLoading }),
    'sitemap-generator': dynamic(() => import('./sitemap-generator/sitemap-generator.component'), { loading: EditorLoading }),
    'robots-txt-generator': dynamic(() => import('./robots-txt-generator/robots-txt-generator.component'), { loading: EditorLoading }),
    'htaccess-generator': dynamic(() => import('./htaccess-generator/htaccess-generator.component'), { loading: EditorLoading }),
    'image-to-base64': dynamic(() => import('./image-to-base64/image-to-base64.component'), { loading: EditorLoading }),
    // S1 family #4 — JSON → type generators (8 routes share one editor; FEATURE_EXPANSION_RESEARCH.md §5.4)
    'convert-json-to-typescript': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-go': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-python': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-rust': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-kotlin': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-java': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-csharp': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    'convert-json-to-jsonschema': dynamic(() => import('./json-to-types/json-to-types.component'), { loading: EditorLoading }),
    // S1 family #4 — HTML table converters (3 routes share one editor)
    'convert-html-table-to-markdown': dynamic(() => import('./html-table-converter/html-table-converter.component'), { loading: EditorLoading }),
    'convert-html-table-to-csv': dynamic(() => import('./html-table-converter/html-table-converter.component'), { loading: EditorLoading }),
    'convert-html-table-to-json': dynamic(() => import('./html-table-converter/html-table-converter.component'), { loading: EditorLoading }),
    // S1 family #4 — cURL converters (4 routes share one editor)
    'convert-curl-to-fetch': dynamic(() => import('./curl-converter/curl-converter.component'), { loading: EditorLoading }),
    'convert-curl-to-httpie': dynamic(() => import('./curl-converter/curl-converter.component'), { loading: EditorLoading }),
    'convert-curl-to-python': dynamic(() => import('./curl-converter/curl-converter.component'), { loading: EditorLoading }),
    'convert-curl-to-wget': dynamic(() => import('./curl-converter/curl-converter.component'), { loading: EditorLoading }),
    // S1 family #4 — dev format formatters (6 routes share one editor)
    'toml-formatter': dynamic(() => import('./dev-format-formatter/dev-format-formatter.component'), { loading: EditorLoading }),
    'ini-formatter': dynamic(() => import('./dev-format-formatter/dev-format-formatter.component'), { loading: EditorLoading }),
    'properties-formatter': dynamic(() => import('./dev-format-formatter/dev-format-formatter.component'), { loading: EditorLoading }),
    'env-formatter': dynamic(() => import('./dev-format-formatter/dev-format-formatter.component'), { loading: EditorLoading }),
    'hcl-formatter': dynamic(() => import('./dev-format-formatter/dev-format-formatter.component'), { loading: EditorLoading }),
    'graphql-formatter': dynamic(() => import('./dev-format-formatter/dev-format-formatter.component'), { loading: EditorLoading }),
    // S1 family #4 — utility generators
    'markdown-toc-generator': dynamic(() => import('./markdown-toc-generator/markdown-toc-generator.component'), { loading: EditorLoading }),
    'slug-generator': dynamic(() => import('./slug-generator/slug-generator.component'), { loading: EditorLoading }),
    'big-number-calculator': dynamic(() => import('./big-number-calculator/big-number-calculator.component'), { loading: EditorLoading }),
    'roman-numeral-converter': dynamic(() => import('./roman-numeral-converter/roman-numeral-converter.component'), { loading: EditorLoading }),
    'hex-dump-viewer': dynamic(() => import('./hex-dump-viewer/hex-dump-viewer.component'), { loading: EditorLoading }),
    // S1 family #4 — lookup tables
    'http-status-codes': dynamic(() => import('./http-status-codes/http-status-codes.component'), { loading: EditorLoading }),
    'mime-types': dynamic(() => import('./mime-types/mime-types.component'), { loading: EditorLoading }),
    'ascii-table': dynamic(() => import('./ascii-table/ascii-table.component'), { loading: EditorLoading }),
    'unicode-table': dynamic(() => import('./unicode-table/unicode-table.component'), { loading: EditorLoading }),
    // S3 family #5 — calculators (FEATURE_EXPANSION_RESEARCH.md §5.5)
    'bmi-calculator': dynamic(() => import('./bmi-calculator/bmi-calculator.component'), { loading: EditorLoading }),
    'bmr-calculator': dynamic(() => import('./bmr-calculator/bmr-calculator.component'), { loading: EditorLoading }),
    'tdee-calculator': dynamic(() => import('./tdee-calculator/tdee-calculator.component'), { loading: EditorLoading }),
    'body-fat-calculator': dynamic(() => import('./body-fat-calculator/body-fat-calculator.component'), { loading: EditorLoading }),
    'calorie-calculator': dynamic(() => import('./calorie-calculator/calorie-calculator.component'), { loading: EditorLoading }),
    'due-date-calculator': dynamic(() => import('./due-date-calculator/due-date-calculator.component'), { loading: EditorLoading }),
    'percentage-calculator': dynamic(() => import('./percentage-calculator/percentage-calculator.component'), { loading: EditorLoading }),
    'fraction-calculator': dynamic(() => import('./fraction-calculator/fraction-calculator.component'), { loading: EditorLoading }),
    'ratio-calculator': dynamic(() => import('./ratio-calculator/ratio-calculator.component'), { loading: EditorLoading }),
    'mortgage-calculator': dynamic(() => import('./mortgage-calculator/mortgage-calculator.component'), { loading: EditorLoading }),
    'loan-calculator': dynamic(() => import('./loan-calculator/loan-calculator.component'), { loading: EditorLoading }),
    'auto-loan-calculator': dynamic(() => import('./auto-loan-calculator/auto-loan-calculator.component'), { loading: EditorLoading }),
    'refinance-calculator': dynamic(() => import('./refinance-calculator/refinance-calculator.component'), { loading: EditorLoading }),
    'compound-interest-calculator': dynamic(() => import('./compound-interest-calculator/compound-interest-calculator.component'), { loading: EditorLoading }),
    'simple-interest-calculator': dynamic(() => import('./simple-interest-calculator/simple-interest-calculator.component'), { loading: EditorLoading }),
    'retirement-calculator': dynamic(() => import('./retirement-calculator/retirement-calculator.component'), { loading: EditorLoading }),
    '401k-calculator': dynamic(() => import('./401k-calculator/401k-calculator.component'), { loading: EditorLoading }),
    'savings-calculator': dynamic(() => import('./savings-calculator/savings-calculator.component'), { loading: EditorLoading }),
    'paycheck-calculator': dynamic(() => import('./paycheck-calculator/paycheck-calculator.component'), { loading: EditorLoading }),
    'salary-calculator': dynamic(() => import('./salary-calculator/salary-calculator.component'), { loading: EditorLoading }),
    'gpa-calculator': dynamic(() => import('./gpa-calculator/gpa-calculator.component'), { loading: EditorLoading }),
    'grade-calculator': dynamic(() => import('./grade-calculator/grade-calculator.component'), { loading: EditorLoading }),
    'time-card-calculator': dynamic(() => import('./time-card-calculator/time-card-calculator.component'), { loading: EditorLoading }),
    'hours-between-calculator': dynamic(() => import('./hours-between-calculator/hours-between-calculator.component'), { loading: EditorLoading }),
    'working-days-calculator': dynamic(() => import('./working-days-calculator/working-days-calculator.component'), { loading: EditorLoading }),
    'sales-tax-calculator': dynamic(() => import('./sales-tax-calculator/sales-tax-calculator.component'), { loading: EditorLoading }),
    'vat-calculator': dynamic(() => import('./vat-calculator/vat-calculator.component'), { loading: EditorLoading }),
    'gst-calculator': dynamic(() => import('./gst-calculator/gst-calculator.component'), { loading: EditorLoading }),
    'sunrise-sunset-calculator': dynamic(() => import('./sunrise-sunset-calculator/sunrise-sunset-calculator.component'), { loading: EditorLoading }),
    'tip-calculator': dynamic(() => import('./tip-calculator/tip-calculator.component'), { loading: EditorLoading }),
    'discount-calculator': dynamic(() => import('./discount-calculator/discount-calculator.component'), { loading: EditorLoading }),
    'cidr-calculator': dynamic(() => import('./cidr-calculator/cidr-calculator.component'), { loading: EditorLoading }),
    'subnet-calculator': dynamic(() => import('./subnet-calculator/subnet-calculator.component'), { loading: EditorLoading }),
    // S3 family #6 (xconvert-web slice) — ISO-8601 validator
    'iso-8601-validator': dynamic(() => import('./iso-8601-validator/iso-8601-validator.component'), { loading: EditorLoading }),
    // S2 family #2 — color extras + CSS generators (FEATURE_EXPANSION_RESEARCH.md §5.2)
    'gradient-generator': dynamic(() => import('./gradient-generator/gradient-generator.component'), { loading: EditorLoading }),
    'contrast-checker': dynamic(() => import('./contrast-checker/contrast-checker.component'), { loading: EditorLoading }),
    'triangle-generator': dynamic(() => import('./triangle-generator/triangle-generator.component'), { loading: EditorLoading }),
    'box-shadow-generator': dynamic(() => import('./box-shadow-generator/box-shadow-generator.component'), { loading: EditorLoading }),
    'border-radius-generator': dynamic(() => import('./border-radius-generator/border-radius-generator.component'), { loading: EditorLoading }),
    'clip-path-generator': dynamic(() => import('./clip-path-generator/clip-path-generator.component'), { loading: EditorLoading }),
    'glassmorphism-generator': dynamic(() => import('./glassmorphism-generator/glassmorphism-generator.component'), { loading: EditorLoading }),
    'neumorphism-generator': dynamic(() => import('./neumorphism-generator/neumorphism-generator.component'), { loading: EditorLoading }),
    'flexbox-generator': dynamic(() => import('./flexbox-generator/flexbox-generator.component'), { loading: EditorLoading }),
    'css-grid-generator': dynamic(() => import('./css-grid-generator/css-grid-generator.component'), { loading: EditorLoading }),
    'css-animation-generator': dynamic(() => import('./css-animation-generator/css-animation-generator.component'), { loading: EditorLoading }),
    'css-filter-generator': dynamic(() => import('./css-filter-generator/css-filter-generator.component'), { loading: EditorLoading }),
    'css-transform-generator': dynamic(() => import('./css-transform-generator/css-transform-generator.component'), { loading: EditorLoading }),
    'tailwind-palette-builder': dynamic(() => import('./tailwind-palette-builder/tailwind-palette-builder.component'), { loading: EditorLoading }),
    'material-color-tool': dynamic(() => import('./material-color-tool/material-color-tool.component'), { loading: EditorLoading }),
    'pantone-converter': dynamic(() => import('./pantone-converter/pantone-converter.component'), { loading: EditorLoading }),
    'palette-extractor': dynamic(() => import('./palette-extractor/palette-extractor.component'), { loading: EditorLoading }),
    'eyedropper': dynamic(() => import('./eyedropper/eyedropper.component'), { loading: EditorLoading }),
    'color-blindness-simulator': dynamic(() => import('./color-blindness-simulator/color-blindness-simulator.component'), { loading: EditorLoading }),
    // S2 family #7 — random / fake data + crypto utilities (FEATURE_EXPANSION_RESEARCH.md §5.7)
    'nanoid-generator': dynamic(() => import('./nanoid-generator/nanoid-generator.component'), { loading: EditorLoading }),
    'ulid-generator': dynamic(() => import('./ulid-generator/ulid-generator.component'), { loading: EditorLoading }),
    'ksuid-generator': dynamic(() => import('./ksuid-generator/ksuid-generator.component'), { loading: EditorLoading }),
    'fake-name-generator': dynamic(() => import('./fake-name-generator/fake-name-generator.component'), { loading: EditorLoading }),
    'fake-email-generator': dynamic(() => import('./fake-email-generator/fake-email-generator.component'), { loading: EditorLoading }),
    'fake-address-generator': dynamic(() => import('./fake-address-generator/fake-address-generator.component'), { loading: EditorLoading }),
    'fake-phone-generator': dynamic(() => import('./fake-phone-generator/fake-phone-generator.component'), { loading: EditorLoading }),
    'random-ip': dynamic(() => import('./random-ip/random-ip.component'), { loading: EditorLoading }),
    'random-string': dynamic(() => import('./random-string/random-string.component'), { loading: EditorLoading }),
    'random-number': dynamic(() => import('./random-number/random-number.component'), { loading: EditorLoading }),
    'random-color': dynamic(() => import('./random-color/random-color.component'), { loading: EditorLoading }),
    'mock-json-generator': dynamic(() => import('./mock-json-generator/mock-json-generator.component'), { loading: EditorLoading }),
    'hmac-generator': dynamic(() => import('./hmac-generator/hmac-generator.component'), { loading: EditorLoading }),
    'bcrypt-generator': dynamic(() => import('./bcrypt-generator/bcrypt-generator.component'), { loading: EditorLoading }),
    'argon2-generator': dynamic(() => import('./argon2-generator/argon2-generator.component'), { loading: EditorLoading }),
    'scrypt-generator': dynamic(() => import('./scrypt-generator/scrypt-generator.component'), { loading: EditorLoading }),
    'jwt-encoder': dynamic(() => import('./jwt-encoder/jwt-encoder.component'), { loading: EditorLoading }),
    'x509-decoder': dynamic(() => import('./x509-decoder/x509-decoder.component'), { loading: EditorLoading }),
    'convert-pem-to-der': dynamic(() => import('./convert-pem-to-der/convert-pem-to-der.component'), { loading: EditorLoading }),
    'convert-der-to-pem': dynamic(() => import('./convert-der-to-pem/convert-der-to-pem.component'), { loading: EditorLoading }),
    // S6 family #10 — PDF Studio (all 8 verb URLs share one editor)
    'rotate-pdf': pdfStudioEditor,
    'watermark-pdf': pdfStudioEditor,
    'add-page-numbers-pdf': pdfStudioEditor,
    'delete-pages-pdf': pdfStudioEditor,
    'extract-pdf-pages': pdfStudioEditor,
    'reorder-pdf': pdfStudioEditor,
    'n-up-pdf': pdfStudioEditor,
    'flatten-pdf': pdfStudioEditor,
    // S6 family #11 — Edit PDF. Mounted only through the provenance gate in getEditor().
    'pdf-editor': pdfEditorEditor,
};

/**
 * `flags` is the last feature-flag emission (null until the first one arrives), `timedOut`
 * is the gate's own 8s deadline and `workspaceActive` is whether an opted-in editor has a
 * document open. Declared as a state type because this class previously had none —
 * `this.state` was never touched, so the first `setState` would otherwise be untyped.
 * (T0b, 2026-09-02)
 */
interface SinglePageBaseState {
    flags: FeatureFlagsEmission | null;
    timedOut: boolean;
    workspaceActive: boolean;
    /**
     * Is this a surface on which an open editor takes the whole viewport? (R1 round 1 §D.)
     *
     * State and not a getter, because it has to survive a ROTATION: a phone turned from portrait
     * to landscape crosses no React boundary, and a page body that reappeared only on the next
     * unrelated render would be a body that comes and goes. `false` at construction so the server
     * render and the static export are the full marketing page — see `showPageChromeAroundEditor`.
     */
    ownsViewport: boolean;
}

export default class SinglePageBaseComponent extends Component<RouteProps, SinglePageBaseState> {
    public title: string;
    public description: string;
    public features: Array<SeoFeature>;

    public showHTML = false;
    public isMobile: boolean;

    public feature;
    public processType: string;
    featureDescriptionsDisabled = false;
    /** The gate's 8s deadline handle; cleared on unmount so a late fire cannot setState. */
    private flagDeadline: ReturnType<typeof setTimeout> | undefined;

    @lazyInject(MotivesService) public motivesService: MotivesService;
    @lazyInject(EnvironmentService) public env: EnvironmentService;
    @lazyInject(SettingsService) public settingsService: SettingsService;

    get showRating() {
        return this.env?.sitename === this.env?.env.constants.ps2pdfId;
    }

    get showInfoGrid() {
        return this.env?.sitename === this.env?.env.constants.ps2pdfId;
    }

    constructor(props) {
        super(props);
        const value = props.pageData;
        this.processType = value.processType;
        this.features = value.page_seo.feature;
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.feature = RouteUtils.getPathFromProps(props);
        this.state = { flags: null, timedOut: false, workspaceActive: false,
            // Never `editorOwnsViewport()` here: the constructor runs on the server too, and a
            // value that differs between the server and the first client render is a hydration
            // mismatch. `componentDidMount` is the first moment there is a viewport to ask.
            ownsViewport: false };
    }

    /** The viewport-ownership listener's teardown, or `undefined` when this page never armed
     *  one (every tool but /pdf-editor). */
    private ownsViewportOff: (() => void) | undefined;

    componentDidMount() {
        /*
         * THE MARKETING PAGE DOES NOT RENDER UNDERNEATH AN OPEN DOCUMENT (R1 round 1 §D item 4).
         *
         * Only /pdf-editor, for the same reason the flag gate below is: this shell is ~200 pages
         * plus eight Studio verbs, and a media-query listener on all of them buys each one a
         * re-render for a value none of them reads.
         *
         * A listener and not a one-shot read, because a rotation crosses no React boundary — and
         * `matchMedia` is feature-checked because this file renders under jsdom and under the
         * static export, where it may be absent entirely.
         */
        if (this.feature === 'pdf-editor' && typeof window !== 'undefined'
            && typeof window.matchMedia === 'function') {
            try {
                const mq = window.matchMedia(VIEWPORT_OWNED_MEDIA_QUERY);
                const sync = () => {
                    const owns = editorOwnsViewport();
                    if (this.state.ownsViewport !== owns) this.setState({ ownsViewport: owns });
                };
                sync();
                // `addEventListener` where it exists; Safari < 14 only has `addListener`.
                if (typeof mq.addEventListener === 'function') {
                    mq.addEventListener('change', sync);
                    this.ownsViewportOff = () => mq.removeEventListener('change', sync);
                } else if (typeof (mq as unknown as { addListener?: unknown }).addListener === 'function') {
                    (mq as unknown as { addListener: (f: () => void) => void }).addListener(sync);
                    this.ownsViewportOff = () =>
                        (mq as unknown as { removeListener: (f: () => void) => void })
                            .removeListener(sync);
                }
            } catch { /* a media query this browser cannot parse: the desktop page stands */ }
        }
        this.settingsService.userSettings.pipe(untilDestroyed(this)).subscribe({
            next: (userSettings) => {
                this.featureDescriptionsDisabled = get(userSettings, 'featureDescriptionsDisabled', false);
            },
        });
        // ONLY /pdf-editor has a gate, so only /pdf-editor pays for it (2026-09-02). This shell is
        // every single-page tool (~200 pages) plus all eight PDF Studio verbs; subscribing and
        // arming a timer on all of them would buy each an extra setState re-render for a value
        // none of them reads.
        if (this.feature === 'pdf-editor') {
            // For the page's lifetime — never take(1). The props emission lands first and the
            // api emission can arrive much later; the gate has to see both.
            this.settingsService.featureFlags$.pipe(untilDestroyed(this)).subscribe({
                next: (emission) => this.setState({ flags: emission }),
            });
            // The settings service deliberately never re-emits or errors on a failed API call
            // (its error path only logs), so the gate carries its own deadline: after 8s we
            // render the PROPS value, which is prod's truth after the prod flip. A settings 5xx
            // must show the editor, not a false "coming soon", and never a permanent skeleton.
            // No EDIT_PDF_FAIL — this is not a document failure.
            this.flagDeadline = setTimeout(() => this.setState({ timedOut: true }), 8000);
        }
    }

    componentDidUpdate() {
        // The editor stopped being mounted (an api emission flipped pdfEditor true->false while
        // a document was open). `workspaceActive` below already restores the hero and top slot
        // for THIS render; clearing the latch keeps it from outliving the editor that set it,
        // so a later remount starts with the hero showing rather than a stale file chip.
        // Guarded on the latch itself, so this can never loop. (2026-09-02)
        if (this.state.workspaceActive && this.gateVerdict !== 'editor') {
            this.setState({ workspaceActive: false });
        }
    }

    componentWillUnmount() {
        this.ownsViewportOff?.();
        clearTimeout(this.flagDeadline);
    }

    /**
     * The /pdf-editor gate's three-way verdict, computed from the last emission rather than
     * latched, so getEditor() and workspaceActive can never disagree about whether the editor
     * is on screen. `props` always carries PROD's flag set (xconvert-web-ssr bakes the prod api
     * host into every build), so a props-sourced value is not evidence about this environment
     * and yields 'wait' — a neutral placeholder, not "coming soon". (2026-09-02)
     */
    private get gateVerdict(): 'wait' | 'editor' | 'off' {
        const { flags, timedOut } = this.state;
        if (!flags) return 'wait';
        if (flags.source === 'props' && !timedOut) return 'wait';
        return flags.flags.pdfEditor === true ? 'editor' : 'off';
    }

    /**
     * Workspace mode applies only while the editor that asked for it is actually mounted.
     * Derived, not read straight off state: if the flag flips true->false with a document open
     * the editor is replaced by the flag-off panel, and a page showing a file chip and no hero
     * next to "Editing PDF text is on the way" would be incoherent. (2026-09-02)
     */
    private get workspaceActive(): boolean {
        // The four terms are unchanged; they are now expressed through the shared leaf so the two
        // page bases cannot drift into two mechanisms (P5b.1's `utils/workspace-mode.ts`, which
        // was a MIRROR of this getter until this edit made it the source). `requested` is the
        // opt-in; `editorMounted` is rule 4 -- workspace chrome only while the editor that asked
        // for it is really on screen. `workspace-mode.spec.ts` now fails if EITHER page base
        // inlines the recipe again. (P5c.1a, 2026-09-17)
        return isWorkspaceActive({
            requested: this.state.workspaceActive
                && this.feature === 'pdf-editor'
                && !!editorMap[this.feature],
            editorMounted: this.gateVerdict === 'editor',
        });
    }

    // Workspace mode (spec §7.2, 2026-09-02). Opt-in: only the /pdf-editor entry passes
    // supportsWorkspaceMode, so no other converter can lose its revenue surface by accident.
    // The chip REPLACES the hero rather than hiding it, because a page with a loaded document
    // and no title reads as broken; QA row 28 locates it by data-testid, row 28b proves the
    // blast radius is zero.
    onWorkspaceChange = (active: boolean) => {
        if (this.state?.workspaceActive !== active) this.setState({ workspaceActive: active });
    };

    getEditor() {
        const Editor = editorMap[this.feature];
        if (!Editor) return null;
        if (this.feature !== 'pdf-editor') return <Editor feature={this.feature} />;

        const verdict = this.gateVerdict;
        if (verdict === 'wait') return <EditPdfPlaceholder />;
        if (verdict === 'off') return <EditPdfComingSoon />;
        return <Editor
            feature={this.feature}
            onWorkspaceChange={this.onWorkspaceChange}
            supportsWorkspaceMode={true}
        />;
    }

    render() {
        // The rails go with the hero and the top slot (spec §7.2). They stayed on with a
        // document open, and the right pane sat over the selection popover: QA pass 1 rows 5, 8
        // and 9 all failed on "right-pane intercepts pointer events" over Delete, not on
        // anything the editor did. `MotiveFrame` has supported this since it was written; the
        // shell simply never passed it (QA pass 1 P2, 2026-09-02).
        return <MotiveFrame hideSidePanes={this.workspaceActive}>
            {/* GAP §3 #3 (lane H): with a document open the wrapper keeps 8 of its 40 px and the
                card's top padding goes, so page 1 starts high enough to be seen. Undefined (no
                inline style at all) on the gate, the export and every other converter. */}
            <div className={sharedStyles.wrapper} style={editorOpenWrapperStyle(this.workspaceActive)}>
                {/* Workspace mode lifts the shell to full width so a document viewer is not
                    boxed into the 1200px marketing column. Off for every other page. */}
                <div style={workspaceWrapperStyle(this.workspaceActive)}>
                    {showPageFurniture(this.workspaceActive) && (
                        <div className={`gcontent-container ${styles["gcontent-container mb-3"]}`}>
                            <MotiveComponent
                                data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px"
                                adWidth="970px" adHeight="90px" showAds={true} location={MotiveLocationTypes.singlePageBaseTop}
                                className='width_major'
                            ></MotiveComponent>
                        </div>
                    )}
                    <div className="row nav-padding">
                        <div className="col-xs-12 col-sm-12 col-md-12 service-container">
                            {/* UX-10 (W8-SHELL): /pdf-editor's `main` landmark + skip-link target;
                                a bare fragment on every other converter. */}
                            <SkipTarget active={hasSkipLink(this.feature)}>
                            <div className={sharedStyles.card} style={editorOpenCardStyle(this.workspaceActive)}>
                                {/*
                                    Q4 §2 (2026-09-22): at cold load in landscape the page stack's
                                    top edge sits at `y = 321.19 px` — identical at 360, 390 and
                                    412 px tall — so 10.8 / 17.6 / 22.0 % of the viewport is page
                                    area and NO text run is visible at any of the three. None of
                                    that band collapses for a short viewport. The chip is this
                                    page base's own share of it: with a document open it says the
                                    file name and nothing else, which the editor's top bar already
                                    says, in a row the editor already draws. On a surface that has
                                    given the document the whole viewport it is a line of chrome
                                    bought with the only axis the reader has none of.

                                    The hero itself is untouched — `workspaceActive` is false
                                    until a client event opens a document, so the pre-upload page
                                    and everything a crawler sees are exactly as they were.
                                */}
                                {/* GAP §3 #3 (lane H, 2026-10-04): the tool-name chip no longer
                                    stands above an open document on a desktop either — it was 49 px
                                    of the 330 px before page 1 at 1440x900, naming the page rather
                                    than the file the document bar already names. */}
                                {!showToolNameAboveEditor(this.workspaceActive)
                                    ? null
                                    : <div data-testid="feature-hero" className={`text-center mb-5 ${styles['feature-hero']}`}>
                                        <h1 className={`title ${styles['hero-title']}`}>
                                            {this.title}
                                        </h1>
                                        <p className={`description secondary-text ${styles['hero-description']}`}>
                                            {this.description}
                                        </p>
                                    </div>
                                }
                                {this.getEditor()}
                            </div>
                            </SkipTarget>
                        </div>

                        {/* In-content slot right after the tool result, before the how-to -- the one
                            slot every dev-tool shape has room for (AD_PLACEMENT_DESIGN.md 9). Reuses
                            the in-content role key. Not on the full-width workspace pages. */}
                        {showPageFurniture(this.workspaceActive) && (
                            <div className="col-12 mt-3" style={{ display: 'flex', justifyContent: 'center' }}>
                                <MotiveComponent
                                    data={AdSlots.inContent} containerWidth="100%" containerHeight="280px"
                                    adWidth="336px" adHeight="280px" showAds={true}
                                    location={MotiveLocationTypes.convertPageInContent} className="width_major_md"
                                ></MotiveComponent>
                            </div>
                        )}

                        {/*
                            THE PAGE UNDER THE DOCUMENT (R1 round 1 §D item 4, 2026-09-22).

                            R1 measured what standing here costs on a phone: with no contained
                            scroll region, `document.scrollingElement.scrollHeight` is 25 344 px
                            against a 450 px document, and one 1 520 px flick leaves nothing on
                            screen but the "How to Edit a PDF Online" article — no rail, no sheet,
                            no Save, and no way back except scrolling up 1 500 px. Giving the
                            editor the viewport is only honest if there is not still a marketing
                            page standing behind it, and it is what makes the wave's integration
                            assertion — "no fixed element with a non-editor element behind its
                            centre point" — satisfiable at all.

                            NOTHING IS REMOVED FROM THE PAGE. `showPageChromeAroundEditor` is false
                            only when a document is open in a mounted editor AND this surface
                            gives it the whole viewport. `workspaceActive` is set by a client
                            event after a user picks a file, and `ownsViewport` is `false` on the
                            server by construction — so the static export and every crawler see
                            `/pdf-editor` with the article, the related links, the rating and the
                            tiles exactly as they are today. Desktop keeps them with a document
                            open too, which is where the revenue surfaces live.
                        */}
                        {showPageChromeAroundEditor(this.workspaceActive, this.state.ownsViewport) && <>
                            {this.props.markdown && (
                                <div className="col-12 mt-3">
                                    <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>
                                </div>
                            )}

                            {
                                this.props?.relatedLinks?.length > 0 && <div className="col-12 col-sm-12 col-md-12 col-lg-12 mt-3" >
                                    <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
                                </div>
                            }
                            {
                                this.showRating && <div className="col-12 col-sm-12 col-md-12 col-lg-12 text-center mt-3">
                                    <RatingDisplayComponent
                                        rating={this.props.rating}
                                        title={this.title}
                                        feature={this.feature}
                                        description={this.description}>
                                    </RatingDisplayComponent>
                                </div>
                            }
                            {
                                this.showInfoGrid && !this.featureDescriptionsDisabled && <div className="col-12 col-sm-12 col-md-12 col-lg-12 mt-3">
                                    {
                                        this.features && <FeaturesTilesComponent features={this.features}></FeaturesTilesComponent>
                                    }
                                </div>
                            }
                        </>}
                    </div>
                </div>
            </div>
        </MotiveFrame >
    }
}
